import { remainingCooldownTicks } from '../net/cooldown.js';
import { xpToNextLevel } from '../game/progression.js';
import { skillMasteryXpToNext } from '../game/skill-mastery.js';
import type { CharacterView, MeView, WorldView } from '../game/http-types.js';
import type { PrimaryStatKey } from '../sim/primary-stats.js';
import type { PlayerSnapshot, StatusView, StatusViewKind } from '../net/protocol.js';
import type { ConnectionState } from './connection.js';
import { STATUS_KIND_NAMES } from './status-chips.js';
import { RECALL_CHANNEL_S } from '../game/recall.js';
import type { SkillAimMode } from './input.js';
import { callApi as api, withStaleRetry } from './stale-retry.js';

/** A hotbar slot's text: the skill's name, its Mastery, and how far it is to the next one. */
export function skillSlotLabel(skill: Pick<CharacterView['skills'][number], 'name' | 'masteryLevel' | 'masteryXp'>): string {
  const needed = skillMasteryXpToNext(skill.masteryLevel);
  return `${skill.name} · 숙련 ${skill.masteryLevel}${needed === undefined ? '' : ` (${Math.floor(100 * (skill.masteryXp ?? 0) / needed)}%)`}`;
}

const STAT_KEYS = ['STR', 'AGI', 'TEC', 'VIT', 'MAG', 'WIL'] as const;

export interface ExperienceProgress {
  readonly current: number;
  readonly required: number | null;
  readonly percent: number;
  readonly maxLevel: boolean;
}

/** Project canonical, persisted current-level XP into display values only. */
export function experienceProgress(level: number, xp: number): ExperienceProgress {
  if (level === 100) return { current: 0, required: null, percent: 100, maxLevel: true };
  const required = xpToNextLevel(level);
  return { current: xp, required, percent: Math.max(0, Math.min(100, (xp / required) * 100)), maxLevel: false };
}

/** The HUD shows the debug panel only when the server says dev tools are on; never from a client flag. */
export function debugToolsVisible(me: Pick<MeView, 'devTools'> | undefined): boolean { return me?.devTools === true; }

export interface SkillCooldownView {
  /** Remaining seconds rounded up to a tenth, as shown on the slot. */
  readonly label: string;
  /** How much of the slot the dark overlay covers: remaining / total when the total is known, else 100 (the slot is simply locked). */
  readonly percent: number;
}

/**
 * A slot's cooldown from the server's `cooldownTicks`, or undefined when it is ready.
 *
 * `totalS` is the skill's cooldown from the server's summary (`timing.cooldownS`); the snapshot carries only the
 * remaining ticks. Without a total the overlay stays full and the seconds are the only progress shown.
 */
export function skillCooldownView(ticks: number | undefined, tickRate: number | undefined, totalS: number | undefined): SkillCooldownView | undefined {
  if (ticks === undefined || !Number.isFinite(ticks) || ticks <= 0 || tickRate === undefined) return undefined;
  const remainingS = ticks / tickRate;
  const percent = totalS !== undefined && Number.isFinite(totalS) && totalS > 0 ? Math.max(0, Math.min(100, remainingS / totalS * 100)) : 100;
  return { label: (Math.ceil(ticks * 10 / tickRate) / 10).toFixed(1), percent };
}

/** Recall control text while the cooldown runs, rounded up so it never reads 0 before the server allows a start. */
export function recallCooldownLabel(remainingS: number): string { return '귀환 대기 ' + Math.ceil(remainingS) + '초'; }

const STATUS_CHIP_NAMES: Readonly<Partial<Record<StatusViewKind, string>>> = Object.fromEntries(
  (['slow', 'root', 'stun', 'silence', 'dot'] as const).map((kind) => [kind, STATUS_KIND_NAMES[kind]]),
);
/** One text chip per harmful status kind on the player: the kind with its longest remaining time, rounded up to 0.1 s. Expired and unlimited statuses show nothing. */
export function playerStatusChips(statuses: readonly StatusView[], serverTick: number | undefined, tickRate: number | undefined): readonly string[] {
  if (serverTick === undefined || tickRate === undefined) return [];
  const longest = new Map<StatusViewKind, number>();
  for (const status of statuses) {
    if (status.expireServerTick === null || STATUS_CHIP_NAMES[status.kind] === undefined) continue;
    const ticks = status.expireServerTick - serverTick;
    if (ticks > 0 && ticks > (longest.get(status.kind) ?? 0)) longest.set(status.kind, ticks);
  }
  return Object.keys(STATUS_CHIP_NAMES).filter((kind) => longest.has(kind as StatusViewKind))
    .map((kind) => STATUS_CHIP_NAMES[kind as StatusViewKind] + ' ' + (Math.ceil(longest.get(kind as StatusViewKind)! * 10 / tickRate) / 10).toFixed(1) + '초');
}

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error('missing UI element: ' + id);
  return found as T;
}

export class BrowserUi {
  private readonly auth = element<HTMLElement>('auth');
  private readonly hud = element<HTMLElement>('hud');
  private readonly status = element<HTMLElement>('connection-status');
  private readonly error = element<HTMLElement>('error');
  private readonly hotbar = element<HTMLElement>('hotbar');
  private readonly hpBar = element<HTMLProgressElement>('hp-bar');
  private readonly hpValue = element<HTMLElement>('hp-value');
  private readonly mpBar = element<HTMLProgressElement>('mp-bar');
  private readonly mpValue = element<HTMLElement>('mp-value');
  private readonly dash = element<HTMLElement>('dash-status');
  private readonly statusChips = element<HTMLElement>('status-chips');
  private readonly progress = element<HTMLElement>('progress');
  private readonly xpBar = element<HTMLProgressElement>('xp-bar');
  private readonly xpValue = element<HTMLElement>('xp-value');
  private readonly levelUp = element<HTMLElement>('level-up');
  private readonly weapon = element<HTMLSelectElement>('weapon');
  private readonly respawn = element<HTMLButtonElement>('respawn');
  private readonly debugLevel = element<HTMLInputElement>('debug-level');
  private readonly debugXpRate = element<HTMLInputElement>('debug-xp-rate');
  private readonly debugTools = element<HTMLElement>('debug-tools');
  private readonly respec = element<HTMLButtonElement>('respec');
  private readonly respecTokens = element<HTMLElement>('respec-tokens');
  private readonly weaponEnhance = element<HTMLButtonElement>('weapon-enhance');
  private readonly weaponEnhanceTokens = element<HTMLElement>('weapon-enhance-tokens');
  private readonly weaponEnhanceStep = element<HTMLElement>('weapon-enhance-step');
  private readonly recall = element<HTMLElement>('recall');
  private readonly recallBar = element<HTMLProgressElement>('recall-bar');
  private recallEndsAtMs: number | undefined;
  private recallCooldownEndsAtMs: number | undefined;
  private character: CharacterView | undefined;
  private accountId: string | undefined;
  private aimModes: SkillAimMode[] = ['AUTO', 'AUTO', 'AUTO', 'AUTO'];
  private sessionVersion = 0;
  private refreshing = false;
  private lastRefresh = 0;
  private pending = false;
  private authenticated = false;
  private tickRate: number | undefined;
  /** Per owned skill id, from the server's summaries: the cooldown total and mana cost the snapshot does not carry. */
  private skillFigures = new Map<string, { cooldownS: number | undefined; manaCost: number | undefined }>();
  /**
   * Extra overlays that also take the keyboard away from the game.
   *
   * `isOverlayActive` used to mean "the login panel is up", which was the
   * only overlay there was. Registering rather than hard-coding keeps the
   * game loop asking one question — is anything covering the game? — instead
   * of growing a list of panels it has to know about.
   */
  private readonly overlays: (() => boolean)[] = [];
  private readonly sessionListeners: (() => void)[] = [];

  constructor(private readonly onSession: (authenticated: boolean) => void) {
    element<HTMLButtonElement>('logout').addEventListener('click', () => { void this.mutate<Record<string, never>>('/api/logout', {}).then((result) => { if (result) this.setAuthenticated(false); }); });
    this.respawn.addEventListener('click', () => { if (this.character) void this.mutate<CharacterView>('/api/respawn', { revision: this.character.revision }).then((character) => this.setCharacter(character)); });
    this.weapon.addEventListener('change', () => { if (this.character) void this.mutate<CharacterView>('/api/weapon', { revision: this.character.revision, weaponId: this.weapon.value }).then((character) => this.setCharacter(character)); });
    for (const key of STAT_KEYS) element<HTMLButtonElement>('stat-' + key).addEventListener('click', () => { if (this.character) void this.allocate(key); });
    this.weaponEnhance.addEventListener('click', () => { if (this.character) void this.mutate<CharacterView>('/api/weapon/enhance', { revision: this.character.revision }).then((character) => this.setCharacter(character)); });
    this.respec.addEventListener('click', () => { if (this.character) void this.mutate<CharacterView>('/api/respec', { revision: this.character.revision }).then((character) => this.setCharacter(character)); });
    for (let slot = 0; slot < 4; slot += 1) {
      element<HTMLButtonElement>('skill-aim-' + (slot + 1)).addEventListener('click', () => {
        this.aimModes[slot] = this.aimModes[slot] === 'MOUSE' ? 'AUTO' : 'MOUSE';
        this.saveAimModes();
        this.renderAimModes();
      });
    }
    // Debug tools. Each one is the same mutate/refresh path the HUD already
    // uses, so a rejection (not resting, stale revision) surfaces in the same
    // error line as any other refused request.
    element<HTMLButtonElement>('debug-recall').addEventListener('click', () => { void this.mutate<Record<string, never>>('/api/debug/recall', {}); });
    element<HTMLButtonElement>('debug-stats-reset').addEventListener('click', () => { if (this.character) void this.mutate<CharacterView>('/api/debug/stats/reset', { revision: this.character.revision }).then((character) => this.setCharacter(character)); });
    this.debugLevel.addEventListener('keydown', (event) => { if (event.key === 'Enter') element<HTMLButtonElement>('debug-level-apply').click(); });
    element<HTMLButtonElement>('debug-level-apply').addEventListener('click', () => {
      if (!this.character) return;
      const level = Number(this.debugLevel.value);
      if (!Number.isSafeInteger(level) || level < 1 || level > 100) { this.showError('레벨은 1에서 100 사이여야 합니다'); return; }
      void this.mutate<CharacterView>('/api/debug/level', { revision: this.character.revision, level }).then((character) => this.setCharacter(character));
    });
    element<HTMLButtonElement>('debug-xp-rate-apply').addEventListener('click', () => {
      if (!this.character) return;
      const rate = Number(this.debugXpRate.value);
      if (!Number.isFinite(rate) || rate <= 0) { this.showError('경험치 배율은 0보다 큰 수여야 합니다'); return; }
      void this.mutate<CharacterView>('/api/debug/xp-rate', { revision: this.character.revision, rate }).then((character) => this.setCharacter(character));
    });
  }

  setWorld(world: WorldView): void {
    this.tickRate = Number.isFinite(world.tickRate) && world.tickRate > 0 ? world.tickRate : undefined;
    this.weapon.replaceChildren(...world.weapons.map((id) => { const option = document.createElement('option'); option.value = id; option.textContent = world.craftableWeapons.includes(id) ? id : `${id} · 스킬 제작 준비 중`; return option; }));
    if (this.character) this.weapon.value = this.character.weaponId;
  }

  setConnection(state: ConnectionState): void {
    const labels: Record<ConnectionState, string> = { 'logged-out': '로그아웃', connecting: '연결 중', connected: '연결됨', retrying: '재연결 중', offline: '오프라인' };
    this.status.textContent = labels[state];
    if (state === 'logged-out') this.setAuthenticated(false);
    this.updateControls();
  }

  setMe(me: MeView): void {
    if (this.accountId !== me.account.id) {
      this.sessionVersion += 1;
      this.notifySessionChanged();
      this.accountId = me.account.id;
      this.character = undefined;
      this.clearCharacterView();
      this.aimModes = loadAimModes(me.account.id);
      this.renderAimModes();
    } else if (this.character && me.character.revision < this.character.revision) {
      return;
    }
    this.skillFigures = new Map((me.summaries ?? []).map((entry) => [entry.skillId, { cooldownS: entry.timing?.cooldownS, manaCost: entry.now?.manaCost }]));
    this.setAuthenticated(true);
    this.debugTools.hidden = !debugToolsVisible(me);
    this.setRecallRemaining(me.recallRemainingS);
    this.recallCooldownEndsAtMs = me.recallCooldownRemainingS === null ? undefined : Date.now() + me.recallCooldownRemainingS * 1000;
    this.renderRecall();
    this.setCharacter(me.character);
    element('account-name').textContent = me.account.username;
  }

  /** Each skill slot's aim: automatic (the default) or the pointer. A per-browser setting of the slot. */
  skillAimModes(): readonly SkillAimMode[] { return this.aimModes; }

  private renderAimModes(): void {
    for (let slot = 0; slot < 4; slot += 1) {
      const button = element<HTMLButtonElement>('skill-aim-' + (slot + 1));
      button.textContent = this.aimModes[slot] === 'MOUSE' ? '마우스 조준' : '자동 조준';
      button.className = this.aimModes[slot] === 'MOUSE' ? 'skill-aim mouse' : 'skill-aim';
    }
  }

  private saveAimModes(): void {
    if (this.accountId === undefined) return;
    try { localStorage.setItem(aimStorageKey(this.accountId), JSON.stringify(this.aimModes)); } catch { /* storage unavailable: the setting lasts this page only */ }
  }

  /** B: start the recall channel, or cancel it when one is already running. The server owns the timer. */
  toggleRecall(): void {
    if (!this.authenticated) return;
    if (this.recallEndsAtMs !== undefined) {
      this.setRecallRemaining(null);
      void this.mutate<{ remainingS: number | null }>('/api/recall/cancel', {});
      return;
    }
    void this.mutate<{ remainingS: number | null }>('/api/recall/start', {}).then((result) => { if (result) this.setRecallRemaining(result.remainingS); });
  }

  private setRecallRemaining(remainingS: number | null): void {
    this.recallEndsAtMs = remainingS === null ? undefined : Date.now() + remainingS * 1000;
    this.renderRecall();
  }

  private renderRecall(): void {
    const remainingMs = this.recallEndsAtMs === undefined ? undefined : Math.max(0, this.recallEndsAtMs - Date.now());
    const cooldownMs = this.recallCooldownEndsAtMs === undefined ? 0 : Math.max(0, this.recallCooldownEndsAtMs - Date.now());
    if (cooldownMs === 0) this.recallCooldownEndsAtMs = undefined;
    this.recall.hidden = remainingMs === undefined && cooldownMs === 0;
    this.recallBar.hidden = remainingMs === undefined;
    if (remainingMs === undefined) {
      if (cooldownMs > 0) element('recall-status').textContent = recallCooldownLabel(cooldownMs / 1000);
      return;
    }
    this.recallBar.max = RECALL_CHANNEL_S * 1000;
    this.recallBar.value = RECALL_CHANNEL_S * 1000 - remainingMs;
    element('recall-status').textContent = '귀환 중 ' + (remainingMs / 1000).toFixed(1) + '초';
    // The local countdown only draws the bar; the server decides whether it finished or was cancelled.
    if (remainingMs === 0) { this.recallEndsAtMs = undefined; this.recall.hidden = true; void this.refresh(true); }
  }

  private cooldownServerTick = 0;

  setPlayer(player: PlayerSnapshot | undefined, serverTick?: number): void {
    this.cooldownServerTick = serverTick ?? 0;
    this.renderRecall();
    this.renderStatusChips(player, serverTick);
    if (!player) {
      this.hpValue.textContent = 'HP —'; this.hpBar.max = 1; this.hpBar.value = 0;
      this.mpValue.textContent = 'MP —'; this.mpBar.max = 1; this.mpBar.value = 0;
      this.dash.textContent = '대시: —';
      for (let slot = 0; slot < 4; slot += 1) this.renderSlot(slot, undefined);
      this.respawn.hidden = true;
      return;
    }
    this.hpValue.textContent = 'HP ' + Math.ceil(player.hp) + '/' + Math.ceil(player.maxHp);
    this.hpBar.max = Math.max(1, player.maxHp); this.hpBar.value = Math.max(0, player.hp);
    this.mpValue.textContent = 'MP ' + Math.ceil(player.mana) + '/' + Math.ceil(player.maxMana ?? 0);
    this.mpBar.max = Math.max(1, player.maxMana ?? 0); this.mpBar.value = Math.max(0, player.mana);
    for (let slot = 0; slot < 4; slot += 1) this.renderSlot(slot, player);
    const ticks = remainingCooldownTicks(player.commonDashReadyTick, player.commonDashCooldownTicks, this.cooldownServerTick);
    if (ticks === undefined || !Number.isFinite(ticks) || ticks < 0 || this.tickRate === undefined) {
      this.dash.textContent = '대시: —';
    } else if (ticks === 0) {
      this.dash.textContent = '대시: 사용 가능';
    } else {
      const seconds = Math.ceil(ticks * 10 / this.tickRate) / 10;
      this.dash.textContent = '대시: ' + seconds.toFixed(1) + '초';
    }
    this.respawn.hidden = !player.down;
  }
  private renderStatusChips(player: PlayerSnapshot | undefined, serverTick: number | undefined): void {
    const labels = player ? playerStatusChips(player.statuses, serverTick, this.tickRate) : [];
    this.statusChips.hidden = labels.length === 0;
    this.statusChips.replaceChildren(...labels.map((label) => { const chip = document.createElement('span'); chip.className = 'status-chip'; chip.textContent = label; return chip; }));
  }
  private renderSlot(slot: number, player: PlayerSnapshot | undefined): void {
    const id = this.character?.equippedSkillIds[slot] ?? null;
    const figures = id === null ? undefined : this.skillFigures.get(id);
    const cooldown = id === null || !player ? undefined : skillCooldownView(remainingCooldownTicks(player.cooldownReadyTicks?.[slot], player.cooldownTicks?.[slot], this.cooldownServerTick), this.tickRate, figures?.cooldownS);
    const poor = !!player && figures?.manaCost !== undefined && player.mana < figures.manaCost;
    const n = String(slot + 1);
    const bar = element<HTMLProgressElement>('skill-cd-' + n);
    const text = element<HTMLElement>('skill-cd-text-' + n);
    bar.hidden = text.hidden = cooldown === undefined;
    if (cooldown) { bar.max = 100; bar.value = cooldown.percent; text.textContent = cooldown.label; }
    element('slot-' + n).className = 'hotbar-slot' + (id === null ? ' empty' : '') + (poor ? ' poor' : '');
  }
  showError(message: string): void { this.error.textContent = message; }
  isOverlayActive(): boolean { return !this.auth.hidden || this.overlays.some((active) => active()); }
  currentCharacter(): Readonly<CharacterView> | undefined { return this.character; }

  /** Register a panel that blocks game input while it is open. */
  registerOverlay(active: () => boolean): void { this.overlays.push(active); }

  /**
   * Notified when the account changed or the player logged out.
   *
   * Anything holding per-account state has to drop it here; a panel that kept
   * showing the previous account's skills would be showing another player's
   * character.
   */
  onSessionChanged(listener: () => void): void { this.sessionListeners.push(listener); }

  /** The session generation a request started under, for discarding late replies. */
  currentSessionVersion(): number { return this.sessionVersion; }

  /** Accept a character another panel obtained from the server. */
  applyCharacter(character: CharacterView): void { this.setCharacter(character); }

  async refresh(force = false): Promise<void> {
    const now = Date.now(); if (!this.authenticated || this.refreshing || !force && now - this.lastRefresh < 1_000) return;
    const sessionVersion = this.sessionVersion;
    const accountId = this.accountId;
    this.refreshing = true; this.lastRefresh = now;
    try {
      const me = await api<MeView>('/api/me');
      if (this.authenticated && sessionVersion === this.sessionVersion && me.account.id === accountId) this.setMe(me);
    } catch (error) {
      if (this.authenticated && sessionVersion === this.sessionVersion) this.showError(error instanceof Error ? error.message : '상태 갱신 실패');
    } finally { this.refreshing = false; }
  }

  private async allocate(stat: PrimaryStatKey): Promise<void> {
    if (!this.character) return;
    const character = await this.mutate<CharacterView>('/api/stats', { revision: this.character.revision, stat, count: 1 });
    if (character) this.setCharacter(character);
  }

  private async mutate<T>(route: string, body: object): Promise<T | undefined> {
    const sessionVersion = this.sessionVersion;
    this.showError(''); this.setPending(true);
    try {
      const result = 'revision' in body && typeof body.revision === 'number'
        ? await withStaleRetry((revision) => api<T>(route, { ...body, revision }), body.revision, async () => (await api<MeView>('/api/me')).character.revision)
        : await api<T>(route, body);
      return sessionVersion === this.sessionVersion ? result : undefined;
    } catch (error) {
      if (sessionVersion === this.sessionVersion) this.showError(error instanceof Error ? error.message : '요청 실패');
      return undefined;
    } finally { this.setPending(false); }
  }

  private clearCharacterView(): void {
    this.debugTools.hidden = true;
    this.recallEndsAtMs = undefined; this.recallCooldownEndsAtMs = undefined; this.recall.hidden = true;
    this.respecTokens.textContent = '—';
    this.weaponEnhanceTokens.textContent = '—';
    this.weaponEnhanceStep.textContent = '—';
    this.progress.textContent = 'Lv. — · 남은 능력치 —';
    this.xpBar.max = 100;
    this.xpBar.value = 0;
    this.xpValue.textContent = '—';
    this.levelUp.hidden = true;
    this.levelUp.textContent = '';
    this.weapon.value = '';
    for (const key of STAT_KEYS) element('value-' + key).textContent = '—';
    for (let slot = 0; slot < 4; slot += 1) element('skill-' + (slot + 1)).textContent = '비어 있음';
    this.setPlayer(undefined);
  }

  private setAuthenticated(value: boolean): void {
    const changed = this.authenticated !== value;
    this.authenticated = value;
    this.auth.hidden = value;
    this.hud.hidden = !value;
    this.hotbar.hidden = !value;
    if (!value) {
      if (changed || this.accountId !== undefined) { this.sessionVersion += 1; this.notifySessionChanged(); }
      this.accountId = undefined;
      this.character = undefined;
      element('account-name').textContent = '';
      this.clearCharacterView();
    }
    if (changed && !value) this.onSession(false);
    this.updateControls();
  }

  private setCharacter(character: CharacterView | undefined): void {
    if (!character || this.character && character.revision < this.character.revision) return;
    const previousLevel = this.character?.level;
    this.character = character;
    const xp = experienceProgress(character.level, character.xp);
    this.progress.textContent = 'Lv. ' + character.level + ' · 남은 능력치 ' + character.unspentPoints;
    this.xpBar.max = 100;
    this.xpBar.value = xp.percent;
    this.xpValue.textContent = xp.maxLevel ? 'MAX' : xp.current + ' / ' + xp.required + ' XP';
    this.levelUp.hidden = true;
    this.levelUp.textContent = '';
    if (previousLevel !== undefined && character.level > previousLevel) {
      this.levelUp.textContent = '레벨 업! Lv. ' + previousLevel + ' → Lv. ' + character.level + ' (+' + (character.level - previousLevel) + ')';
      this.levelUp.hidden = false;
    }
    this.weapon.value = character.weaponId;
    this.respecTokens.textContent = String(character.respecTokens);
    this.weaponEnhanceTokens.textContent = String(character.weaponEnhanceTokens);
    this.weaponEnhanceStep.textContent = String(character.weaponEnhanceStep);
    if (document.activeElement !== this.debugLevel) this.debugLevel.value = String(character.level);
    if (document.activeElement !== this.debugXpRate) this.debugXpRate.value = String(character.xpRate);
    for (const key of STAT_KEYS) element('value-' + key).textContent = String(character.stats[key]);
    for (let slot = 0; slot < 4; slot += 1) { const id = character.equippedSkillIds[slot]; const skill = character.skills.find((entry) => entry.id === id); element('skill-' + (slot + 1)).textContent = skill ? skillSlotLabel(skill) : '비어 있음'; }
    this.updateControls();
  }

  private notifySessionChanged(): void { for (const listener of this.sessionListeners) listener(); }

  private setPending(value: boolean): void { this.pending = value; this.updateControls(); }
  private updateControls(): void {
    // Scoped to the panels this class owns. It used to sweep every button
    // on the page, which was harmless while those were the only panels — but
    // it would now re-enable the crafting dialog's controls wholesale after
    // any HUD request, discarding the states that dialog set for itself
    // (an unavailable option, a submit button with nothing to submit).
    for (const panel of [this.auth, this.hud, this.hotbar]) {
      for (const control of panel.querySelectorAll<HTMLButtonElement | HTMLSelectElement>('button, select')) {
        // `data-self-managed` marks a control whose enabled state belongs to
        // another panel. Sweeping it would fight that owner: the crafting
        // entry point is disabled outside the rest area at the tick rate,
        // and re-enabling it on every HUD refresh made it blink once a
        // second for a player standing outside.
        if (control.dataset.selfManaged !== undefined) continue;
        control.disabled = this.pending;
      }
    }
    this.respec.disabled = this.pending || !this.character || this.character.respecTokens < 1;
    this.weaponEnhance.disabled = this.pending || !this.character || this.character.weaponEnhanceTokens < 1;
    for (const key of STAT_KEYS) element<HTMLButtonElement>('stat-' + key).disabled = this.pending || !this.character || this.character.unspentPoints < 1;
  }
}

function aimStorageKey(accountId: string): string { return 'rpg.skillAim.' + accountId; }

/** The slot aims this browser remembers for an account; automatic for anything missing or unreadable. */
function loadAimModes(accountId: string): SkillAimMode[] {
  let stored: unknown;
  try { stored = JSON.parse(localStorage.getItem(aimStorageKey(accountId)) ?? 'null'); } catch { stored = null; }
  return [0, 1, 2, 3].map((slot) => Array.isArray(stored) && stored[slot] === 'MOUSE' ? 'MOUSE' : 'AUTO');
}
